import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { ButtonStyles } from 'components/Button/Button';

import Button from '../Button';
import DialogBox from '../DialogBox';
import styles from './ConfirmDialog.module.scss';

/**
 * ConfirmDialog creates a confirmation modal. Use it to show confirm boxes to the user, and take action depending on the outcome. It's `open` state should be managed by the consumer.
 */
export default function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  titleText,
  content,
  confirmButtonText,
  cancelButtonText,
  classes,
  confirmButtonDisabled,
  ...otherProps
}) {
  if (!isOpen) {
    return false;
  }

  return (
    <DialogBox
      isOpen
      simpleStyle
      onClose={onCancel}
      centerButtons={
        <>
          <Button
            className={classes.confirmButton || ButtonStyles.ERROR}
            color={
              classes.confirmButton === 'button-primary' ? 'primary' : 'default'
            }
            disabled={confirmButtonDisabled}
            onClick={onConfirm}
            testId="button-confirmDialog-confirm"
          >
            {confirmButtonText}
          </Button>
          <Button
            onClick={onCancel}
            className={classes.cancelButton}
            testId="button-confirmDialog-cancel"
          >
            {cancelButtonText}
          </Button>
        </>
      }
      classes={{
        ...classes,
        wrap: classnames(styles.wrap, classes.wrap),
        content: classnames(styles.content, classes.content),
        header: classnames(styles.header, classes.header),
      }}
      titleText={titleText}
      testids={{
        cancelButton: 'icon-confirmDialog-cancel',
      }}
      {...otherProps}
    >
      <div className="message">{content}</div>
    </DialogBox>
  );
}

ConfirmDialog.propTypes = {
  /**
   * open state of the dialog
   */
  isOpen: PropTypes.bool,
  /**
   * function to run when confirmed
   */
  onConfirm: PropTypes.func.isRequired,
  /**
   * function to run when cancelled
   */
  onCancel: PropTypes.func.isRequired,
  /**
   * title for the dialog
   */
  titleText: PropTypes.node.isRequired,
  /**
   * content of the dialog. can take arbitrary JSX
   */
  content: PropTypes.node.isRequired,
  /**
   * text of the confirm button
   */
  confirmButtonText: PropTypes.node.isRequired,
  /**
   * text of the cancel button
   */
  cancelButtonText: PropTypes.node.isRequired,
  /**
   * whether confirm button is disabled
   */
  confirmButtonDisabled: PropTypes.node,
  /**
   * classNames to add to sections of the ConfirmDialog
   */
  classes: PropTypes.shape({
    confirmButton: PropTypes.string,
    cancelButton: PropTypes.string,
    wrap: PropTypes.string,
    content: PropTypes.string,
    header: PropTypes.string,
  }),
};

ConfirmDialog.defaultProps = {
  isOpen: false,
  confirmButtonDisabled: false,
  classes: {},
};

ConfirmDialog.displayName = 'ConfirmDialog';
